

import pedalImage from "../../../static/images/TSDD/pedals.webp"
import { Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, AccordionActions, Button, Collapse } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import "./pedals.scss"
import { useEffect, useRef, useState } from "react";
import { FullPagePager } from "../../Utiliities/FullScreenWrapper";

function useInView({ threshold = 0.15, root = null, rootMargin = "0px", once = false } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    // Graceful fallback if IntersectionObserver isn't available
    if (typeof window !== "undefined" && !("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold, root, rootMargin }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, root, rootMargin, once]);

  return { ref, inView };
}

const PedalDescription = ({ title, description, once = true }) => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    root: null,
    rootMargin: "0px 0px -10% 0px",
    once, // true = expand once and stay open; false = open when in view, close when out
  });

  return (
    <Box
      ref={ref}
      sx={{
        transition: "transform 0.35s ease, background-color 0.35s ease",
        mb: 2,
        p: 1,
        borderRadius: 2,
        // subtle card-like feel when in view
        ...(true && {
          backgroundColor: "rgba(0,0,0,0.05)",
          transform: "scale(1.02)",
        }),
      }}
    >
      <Typography variant="h6" sx={{ textAlign: "left" }}>
        <b>{title}</b>
      </Typography>

      <Collapse in={true} timeout={450}>
        <Typography variant="h6" sx={{ textAlign: "left", mt: 0.5 }}>
          {description}
        </Typography>
      </Collapse>
    </Box>
  );
};

const PrePredals = () => {
  return <>
    <Box sx={{ pb: 8, pt: 2, background: "#2f99c1", justifyContent: "center", alignItems: "center" }} >
      <Box sx={{ pl: 5, pr: 5, zIndex: 1000, width: "100%", justifyContent: "center" }}>
        <Typography variant="h2" sx={{ zIndex: 2, textAlign: "center", color: "white !important" }}>The Pedalboard</Typography>
        <Typography variant="h4" sx={{ textAlign: "center" }}><b>Your Tone Starts Here</b></Typography>
        <br />
        <Typography variant="h6" sx={{ textAlign: "center" }}>Your rig is not complete without a well filled out pedalboard. We selected a handful of <i>must have</i> pedals in this suite to help you create the tone you're looking for.</Typography>
        <br />
      </Box>
      <Grid container justifyContent={"center"} sx={{ background: "#2f99c1" }}>
        <Grid item xs={12} md={12} sx={{ zIndex: 0 }} >
          <img style={{ zIndex: -10 }} className={"img-fade-top-bottom"} src={pedalImage} width="100%" />
        </Grid>

      </Grid>
      <Grid container justifyContent={"center"} sx={{ pl: 5, pr: 5, background: "#2f99c1" }}>
        <Grid item xs={12} md={12} lg={12}>
          <Grid container spacing={{ xs: 0, md: 6 }}>
            <Grid item xs={12} md={6}>
              <PedalDescription
                title="SF 90 Phaser"
                description="From subtle modulation to full blown crazyness, this phaser can do it all with just one knob. Use it sparingly to add some flavour to your sound, or go all in and let it swirl your tone into a kaleidoscope of motion."
              />

              <PedalDescription
                title="VE-8 Equalizer"
                description="Not just any EQ pedal, this 8 band EQ let's you add an optional drive stage. This allows you to create a custom overdrive sound with an EQ shape exactly like you want it. Use it to shape your pickup sound, or to simply boost the front end of the amplifier."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PedalDescription
                title="Dino Compressor"
                description="Add some pump to your sound. Don't be fooled by the simplicity of this compressor, it is a powerful tool in your arsenal. It can add tons of sustain to your lead sound, or give that snappy feel to your clean tones."
              />

              <PedalDescription
                title="OD296 Overdrive"
                description={
                  <>
                    A pedalboard is not complete without an overdrive. This one is a bit
                    more special than you're used to. The <b><i>Drive</i></b> switch lets
                    you bypass the overdrive circuit, turning the pedal into a clean
                    boost instead. The <b><i>Fat</i></b> switch controls the amount of
                    lowend being filtered in the pedal, turning it from modern
                    aggressiveness to vintage warmth with the flick of a switch.
                  </>
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box >
  </>
}

export default PrePredals;
